import { SalesMetroAdapters } from '../adapters';
import { SalesMetrosApi, SALES_METROS_MAX_PAGE, SALES_METROS_PAGE_LIMIT } from '../clients';

/**
 * The whole sales metro list.
 *
 * **Paged to exhaustion, not truncated.** The list backs dropdowns with no
 * pagination of their own, so a short read would leave metros missing from a
 * filter with no error. Page 1 comes first and the rest are requested only if
 * its `total` says there are more, so a list that fits one page costs exactly
 * one request. `SALES_METROS_MAX_PAGE` caps the fan-out if `total` is wrong.
 *
 * Rows are de-duplicated by id: concurrent pages of an offset-paginated list
 * can overlap if a metro is inserted mid-read.
 *
 * @returns {Promise<import('../types/SalesMetro').ISalesMetro[]>}
 */
export const getSalesMetros = async () => {
  const firstPage = await SalesMetrosApi.getSalesMetros({ page: 1 });

  const items = [...(firstPage?.items ?? [])];
  const total = firstPage?.total ?? items.length;
  // The server echoes the page size it applied; fall back to what we asked for
  // rather than dividing by zero if it ever omits or zeroes the field.
  const limit = firstPage?.limit || SALES_METROS_PAGE_LIMIT;
  const pageCount = Math.min(Math.ceil(total / limit), SALES_METROS_MAX_PAGE);

  const remainingPages = await Promise.all(
    Array.from({ length: Math.max(pageCount - 1, 0) }, (_, index) =>
      SalesMetrosApi.getSalesMetros({ page: index + 2 }),
    ),
  );
  remainingPages.forEach((page) => items.push(...(page?.items ?? [])));

  const byId = new Map(
    SalesMetroAdapters.toSalesMetros(items).map((metro) => [metro.id, metro]),
  );

  return [...byId.values()];
};
